import * as THREE from "three";
import type { CatHandle } from "./cat";
import type { PointOfInterest } from "./pois";

// High-level cat behavior supervisor. The cat module in cat.ts handles
// animation, locomotion, pose, and player-controlled override; this
// layer sits on top and decides *what the cat should be doing right
// now* based on slowly-ticking drives (hunger, thirst, mischief, …).
//
// When a goal becomes active, the supervisor drives the cat via the
// existing `setWaypoint` + `triggerBehavior` API. When no goal is
// active, the cat falls back to its autonomous wander AI.
//
// Goals are pluggable via the `CatGoal` interface — adding a new
// behavior (e.g. "knock cups off the shelf") means writing a new goal
// implementation, not editing this file.

export type DriveId = "hunger" | "thirst" | "mischief";

export interface CatDrives {
  /** 0 (sated) → 1 (urgent). Ticks up over time at `rate` per second. */
  hunger: number;
  thirst: number;
  /** Same scale; cat acts mischievously when this gets high. Phase 2. */
  mischief: number;
}

export interface CatAIContext {
  drives: CatDrives;
  pois: PointOfInterest[];
  cat: CatHandle;
  /** Read-only world-position of the cat (cheaper than reading
   * cat.group.position from inside a hot loop). */
  catPosition: () => THREE.Vector3;
}

export interface CatGoal {
  id: string;
  /**
   * Returns a number representing how urgently this goal wants to run.
   * Negative or zero = not pursuable. The supervisor picks the highest-
   * priority goal each frame, with hysteresis so a goal in progress
   * isn't pre-empted by tiny swings in priority.
   */
  priority(ctx: CatAIContext): number;
  /** Called once when this goal becomes the active goal. */
  begin(ctx: CatAIContext): void;
  /** Called every frame while this is the active goal. Returns true
   *  when complete (the supervisor advances to picking a new goal). */
  tick(ctx: CatAIContext, dt: number): boolean;
  /** Called once when the goal finishes (success or replaced). */
  finish(ctx: CatAIContext): void;
}

export interface CatAIHandle {
  drives: () => Readonly<CatDrives>;
  currentGoalId: () => string | null;
  setEnabled: (enabled: boolean) => void;
}

// Drive growth rates (per second). Tuned so a cat that wakes up hungry
// stays hungry for a couple of minutes — long enough that a player
// notices the cat heading off to eat without the bowls being trampled
// every few seconds.
const HUNGER_RATE = 1 / 150;
const THIRST_RATE = 1 / 110;
const MISCHIEF_RATE = 1 / 220;

// Goal swap threshold — a candidate must beat the active goal's
// priority by this margin to take over. Without it, the cat would
// wobble between food and water if their needs cross during travel.
const PREEMPT_MARGIN = 0.2;

interface ActiveGoal {
  goal: CatGoal;
  priorityAtStart: number;
}

export interface CreateCatAIOptions {
  cat: CatHandle;
  pois: PointOfInterest[];
  goals: CatGoal[];
  /** Register the per-frame tick on the scene update loop. */
  onUpdate: (cb: (dt: number) => void) => void;
  /**
   * Optional override for how often the supervisor re-picks. Defaults
   * to once per second; goals with sub-second logic should drive
   * themselves inside `tick`.
   */
  pickIntervalSeconds?: number;
}

export function createCatAI(opts: CreateCatAIOptions): CatAIHandle {
  const drives: CatDrives = {
    hunger: 0.1,
    thirst: 0.15,
    mischief: 0,
  };

  let active: ActiveGoal | null = null;
  let enabled = true;
  let timeUntilPick = 0;

  const tmpVec = new THREE.Vector3();
  const ctx: CatAIContext = {
    drives,
    pois: opts.pois,
    cat: opts.cat,
    catPosition: () => {
      tmpVec.copy(opts.cat.group.position);
      return tmpVec;
    },
  };

  const pickInterval = opts.pickIntervalSeconds ?? 1.0;

  function selectBestGoal(): CatGoal | null {
    let best: CatGoal | null = null;
    let bestScore = 0;
    for (const goal of opts.goals) {
      const p = goal.priority(ctx);
      if (p > bestScore) {
        bestScore = p;
        best = goal;
      }
    }
    return best;
  }

  opts.onUpdate((dt) => {
    if (!enabled) return;

    drives.hunger = clamp01(drives.hunger + HUNGER_RATE * dt);
    drives.thirst = clamp01(drives.thirst + THIRST_RATE * dt);
    drives.mischief = clamp01(drives.mischief + MISCHIEF_RATE * dt);

    if (active) {
      const done = active.goal.tick(ctx, dt);
      if (done) {
        active.goal.finish(ctx);
        active = null;
        timeUntilPick = 0;
      } else {
        timeUntilPick -= dt;
        if (timeUntilPick <= 0) {
          // Periodic re-evaluation: a much more urgent goal can pre-empt.
          const candidate = selectBestGoal();
          if (
            candidate &&
            candidate !== active.goal &&
            candidate.priority(ctx) > active.goal.priority(ctx) + PREEMPT_MARGIN
          ) {
            active.goal.finish(ctx);
            active = {
              goal: candidate,
              priorityAtStart: candidate.priority(ctx),
            };
            candidate.begin(ctx);
          }
          timeUntilPick = pickInterval;
        }
      }
      return;
    }

    timeUntilPick -= dt;
    if (timeUntilPick > 0) return;
    timeUntilPick = pickInterval;

    const goal = selectBestGoal();
    if (goal) {
      active = { goal, priorityAtStart: goal.priority(ctx) };
      goal.begin(ctx);
    }
  });

  return {
    drives: () => drives,
    currentGoalId: () => active?.goal.id ?? null,
    setEnabled: (v) => {
      enabled = v;
      if (!v && active) {
        active.goal.finish(ctx);
        active = null;
      }
    },
  };
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// ---------------------------------------------------------------------------
// Goal: visit a POI of a given kind, dwell, and zero a drive.
// Used by the food and water goals — same shape, different bindings.
// ---------------------------------------------------------------------------

const ARRIVE_RADIUS = 1.8;

export interface PoiGoalOptions {
  id: string;
  /** Filter — pick the closest POI matching this kind (food / water). */
  poiKind: PointOfInterest["kind"];
  /** Drive that triggers this goal. */
  drive: DriveId;
  /** Below this drive level the goal isn't even considered. */
  threshold: number;
  /** How many seconds the cat dwells at the POI playing its clip. */
  dwellSeconds?: number;
}

export function poiGoal(options: PoiGoalOptions): CatGoal {
  const dwell = options.dwellSeconds ?? 0;

  type Phase = "approaching" | "dwelling";
  let phase: Phase = "approaching";
  let dwellTime = 0;
  let target: PointOfInterest | null = null;

  return {
    id: options.id,
    priority(ctx) {
      const level = ctx.drives[options.drive];
      if (level < options.threshold) return 0;
      // Linear ramp from threshold (priority just above 0) to 1
      // (priority 1) so an urgent need always beats a marginal one.
      return (level - options.threshold) / (1 - options.threshold);
    },
    begin(ctx) {
      target = nearestPoi(ctx.pois, options.poiKind, ctx.catPosition());
      if (!target) return;
      ctx.cat.setWaypoint(target.position);
      phase = "approaching";
      dwellTime = 0;
    },
    tick(ctx, dt) {
      if (!target) return true;
      const pos = ctx.catPosition();
      const dx = target.position.x - pos.x;
      const dz = target.position.z - pos.z;
      const dist = Math.hypot(dx, dz);

      if (phase === "approaching") {
        if (dist < ARRIVE_RADIUS) {
          phase = "dwelling";
          dwellTime = dwell > 0 ? dwell : target.duration;
          ctx.cat.setWaypoint(null);
          ctx.cat.triggerBehavior(target.clip);
        }
        return false;
      }

      // Dwelling — wait out the duration, then sate the drive.
      dwellTime -= dt;
      if (dwellTime <= 0) {
        ctx.drives[options.drive] = 0;
        return true;
      }
      return false;
    },
    finish(ctx) {
      // Defensive cleanup so an interrupted goal doesn't leave the cat
      // walking to a stale waypoint.
      ctx.cat.setWaypoint(null);
      target = null;
      phase = "approaching";
    },
  };
}

// ---------------------------------------------------------------------------
// Default goal set — the supervisor's out-of-the-box behavior. Pulls eat
// and drink so a fresh cat naturally heads for the food bowl, then the
// water bowl, when those drives crest. Adding a new default behavior is
// a one-line edit here, not a wiring change in main.ts.
// ---------------------------------------------------------------------------

export function defaultGoals(): CatGoal[] {
  return [
    poiGoal({ id: "eat", poiKind: "food", drive: "hunger", threshold: 0.55 }),
    poiGoal({
      id: "drink",
      poiKind: "water",
      drive: "thirst",
      threshold: 0.55,
    }),
  ];
}

function nearestPoi(
  pois: PointOfInterest[],
  kind: PointOfInterest["kind"],
  from: THREE.Vector3,
): PointOfInterest | null {
  let best: PointOfInterest | null = null;
  let bestSq = Infinity;
  for (const p of pois) {
    if (p.kind !== kind) continue;
    const dx = p.position.x - from.x;
    const dz = p.position.z - from.z;
    const dsq = dx * dx + dz * dz;
    if (dsq < bestSq) {
      best = p;
      bestSq = dsq;
    }
  }
  return best;
}
